import type { MatrixEvent } from 'matrix-js-sdk';
import { EventType, MsgType } from 'matrix-js-sdk';
import { useMemo } from 'react';
import { useAuthClaims } from '../../hooks/useAuthClaims';
import { useMatrixContext } from '../MatrixContext';
import { getInitialsFromMember, mapMembersToProfiles } from '../utils/memberUtils';
import { useRoomTimeline } from './useRoomTimeline';

export type ConversationMessage = {
  type: string;
  content: string;
  date: Date;
  matrixEvent: MatrixEvent;
};

export type ConversationChunk = {
  initials: string;
  isIncoming: boolean;
  color: string;
  id: string;
  messages: ConversationMessage[];
};

export type Conversation = {
  roomId: string;
  roomName: string;
  chunks: ConversationChunk[];
};

export function useConversations(roomId: string, currentUserId?: string): Conversation {
  const { client } = useMatrixContext();
  const { pimAuthClaims } = useAuthClaims();
  const profileUuid = pimAuthClaims.getProfile().uuid;

  const events = useRoomTimeline(roomId);

  return useMemo(() => {
    if (!client)
      return {
        roomId: '',
        roomName: '',
        chunks: [],
      };

    const room = client.getRoom(roomId);
    if (!room)
      return {
        roomId: '',
        roomName: '',
        chunks: [],
      };

    const members = room.getJoinedMembers();
    const myUserId = currentUserId || client.getUserId();
    const chunks: ConversationChunk[] = [];

    const membersMapping = mapMembersToProfiles(members);

    const profile = membersMapping.filter((m) => m.displayName !== 'bp-backend').find((m) => m.uuid !== profileUuid);

    let lastSenderId: string | null = null;

    for (const matrixEvent of events) {
      // Nur RoomMessage Events mit Text-Content
      if (matrixEvent.getType() !== EventType.RoomMessage) continue;
      if (matrixEvent.getContent().msgtype !== MsgType.Text) continue;

      const senderId = matrixEvent.getSender();
      if (!senderId) continue;

      const sender = members.find((m) => m.userId === senderId);
      const eventDate = matrixEvent.getDate() ?? new Date();
      const eventId = matrixEvent.getId() ?? '';

      const message: ConversationMessage = {
        type: '',
        content: matrixEvent.getContent().body,
        date: eventDate,
        matrixEvent: matrixEvent,
      };
      if (senderId === lastSenderId && chunks.length > 0) {
        chunks[chunks.length - 1].messages.push(message);
      } else {
        // Neue Conversation-Gruppe erstellen
        chunks.push({
          messages: [message],
          isIncoming: senderId !== myUserId,
          color: senderId === myUserId ? 'blue' : 'red',
          initials: getInitialsFromMember(sender),
          id: eventId,
        });
        lastSenderId = senderId;
      }
    }

    return {
      roomId: room.roomId,
      roomName: profile?.displayName ?? '',
      chunks: chunks,
    };
  }, [client, roomId, events, currentUserId, profileUuid]);
}
